import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '../../library/editors/elements/d-edit-text';
import '../../library/editors/elements/d-select-dropdown.js';
import { professionHasSpeciality, specialitiesForProfession } from 'src/store/selectors';
import { t } from 'src/i18n';

import '../../library/elements/d-smooth-resize';
import type { DEditText } from 'src/library/editors/elements/d-edit-text';

/**
 *
 */
@customElement('d-select-profession')
export class DSelectProfession extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
  `;
  @property({ type: Array })
  professions: string[] = [];
  @property({ type: String })
  speciality = '';
  @property({ type: String })
  value = '';
  @property({ type: Boolean })
  custom = false;
  private customOption = t('employees.otherProfession');

  private get showCustom() {
    return this.custom || this.professions.length === 0;
  }

  private get options() {
    if (this.value && !this.professions.includes(this.value)) {
      this.professions.push(this.value);
    }
    this.professions.sort();
    this.professions.push(this.customOption);
    return this.professions.map((x) => ({ value: x, text: x }));
  }

  _hasSpeciality(profession: string) {
    return professionHasSpeciality(profession);
  }

  _computeSpecialities(profession: string) {
    return specialitiesForProfession(profession);
  }

  private setCustom() {
    this.custom = true;
    this.value = '';
    setTimeout(() => {
      const input: DEditText = this.shadowRoot?.getElementById('custom') as DEditText;
      input.setFocus();
    }, 0);
  }

  private onChange() {
    if (professionHasSpeciality(this.value)) {
      if (specialitiesForProfession(this.value).indexOf(this.speciality) === -1) {
        this.speciality = 'Ingen';
      }
    } else {
      this.speciality = 'Ingen';
    }
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.value, speciality: this.speciality },
      }),
    );
  }

  render() {
    return html`
      <div class="sectionElementInner">
        <d-smooth-resize>
          ${this.showCustom
            ? html` <d-edit-text
                id="custom"
                field="employees_profession"
                class="minWidth300"
                placeholder="${t('employees.enterProfession')}"
                .value="${this.value}"
                .button=${this.professions.length > 0}
                .cancelButton=${this.professions.length > 0}
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.value = e.detail.value;
                  this.onChange();
                }}
                @button-click=${(e: CustomEvent<{ value: string }>) => {
                  this.value = e.detail.value;
                  this.custom = false;
                  this.onChange();
                }}
                @cancel-click=${() => {
                  this.value = '';
                  this.custom = false;
                  this.onChange();
                }}
              ></d-edit-text>`
            : html`
                <d-select-dropdown
                  field="employees_profession"
                  .options="${this.options}"
                  placeholder="${t('employees.select')}"
                  .value="${this.value}"
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    e.stopPropagation();
                    this.value = e.detail.value;
                    this.onChange();
                  }}
                >
                </d-select-dropdown>
              `}
          ${this._hasSpeciality(this.value)
            ? html` <d-select-dropdown
                style="margin-top: 8px"
                field="employees_expertise"
                id="speciality"
                .options="${this._computeSpecialities(this.value).map((x) => ({ value: x, text: x }))}"
                .value="${this.speciality}"
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.speciality = e.detail.value;
                  this.onChange();
                }}
              >
              </d-select-dropdown>`
            : nothing}
        </d-smooth-resize>
      </div>
    `;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('value') && this.value === this.customOption) {
      this.setCustom();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-select-profession': DSelectProfession;
  }
}
